<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: '900px' }"
    title="Import Contacts via CSV"
  >
    <div class="q-gutter-y-md">
      <div class="row">
        <InputText
          class="full-width"
          description="Upload a .csv file"
          label="Import file"
          new-design
          type="file"
          @update:model-value="processCsvFile"
        />
      </div>

      <div class="row justify-end">
        <VueJsonCsv :data="sampleContactsCsvData">
          <BaseButton
            flat
            label="Sample contacts.csv"
            new-design
          />
        </VueJsonCsv>
      </div>
    </div>

    <template #buttons>
      <BaseButton
        flat
        label="Cancel"
        new-design
        @click="cancel"
      />
      <BaseButton
        color="primary"
        :disabled="formDisabled"
        :label="importLabel"
        new-design
        @click="confirm"
      />
    </template>
  </Modal>
</template>

<script>
import { camelCase } from "lodash-es";
import Papa from "papaparse";
import VueJsonCsv from "vue-json-csv";

import { BaseButton, InputText } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { pluralize } from "shared/helpers/string";
import {
  ContactMailingList,
  ContactMailingListsContact,
  Influencer,
  Outlet,
} from "shared/resources";

export default {
  name: "ImportContactsModal",
  components: {
    VueJsonCsv,
    BaseButton,
    InputText,
    Modal,
  },
  props: {
    value: {
      type: ContactMailingList,
      required: true,
    },
  },
  emits: ["update:modelValue", "save", "publish", "confirm"],
  data() {
    return {
      contactsToImport: [],
      importStatus: [],
      sampleContactsCsvData: [
        {
          id: "0",
          contactType: "Influencer",
          email: "test@example.com",
          name: "Test",
        },
      ],
    };
  },
  computed: {
    contactMailingList: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    formDisabled() {
      return !this.contactsToImport.length;
    },
    importLabel() {
      const pluralString = pluralize(this.contactsToImport.length, "Contact");

      return `Import ${this.contactsToImport.length} ${pluralString}`;
    },
  },
  methods: {
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.contactsToImport = parsed.map((values) => {
          // set columns uploaded
          const contact = values.reduce((acc, value, index) => {
            let newValue = value;
            if (value === "null" || value === "") newValue = null;
            acc[camelCase(columns[index])] = newValue;

            return acc;
          }, {});

          return contact;
        });
      };

      reader.readAsText(file);
    },
    async verifyContact(contact) {
      try {
        const resource = contact.contactType === "Outlet" ? Outlet : Influencer;
        const contactFound = (await resource.find(contact.id)).data;

        const newRecipient = new ContactMailingListsContact({
          contactMailingListId: this.contactMailingList.id,
          contactId: contactFound.id,
          contactType: contact.contactType,
          contact: contactFound,
        });

        this.contactMailingList.contactMailingListsContacts.push(newRecipient);

        this.importStatus.push({
          contact,
          status: "success",
          message: "ok",
        });
      } catch (error) {
        this.importStatus.push({
          contact,
          status: "failed",
          message: error.response.data.message || error.message,
        });
      }
    },
    async verifyContacts() {
      this.$q.loading.show({ delay: 500 });

      const promises = this.contactsToImport.map((contact) =>
        this.verifyContact(contact)
      );

      await Promise.all(promises);
      this.$q.loading.hide();
    },
    cancel() {
      this.$modals.close("ImportContactsModal");
    },
    async confirm() {
      await this.verifyContacts();

      const successContacts = [];
      const failContacts = [];

      this.importStatus.forEach((contact) => {
        if (contact.status === "success") {
          successContacts.push(contact);
        } else {
          failContacts.push(contact);
        }
      });

      let message = `
        <strong>${successContacts.length} of ${this.contactsToImport.length} uploaded successfully</strong>
      `;

      const failRows = failContacts
        .map(
          (failedContact) =>
            `<tr><td>${failedContact.contact.contactType}</td><td>${failedContact.contact.name}</td><td>${failedContact.message}</td></tr>`
        )
        .join("\n");

      if (failContacts.length) {
        message += `
          <br/><strong>Failed Contacts:</strong><br/>
          <table class="q-table">
            <thead>
              <tr>
                <td>Contact Type</td>
                <td>Name</td>
                <td>Fail Reason</td>
              </tr>
            </thead>
            <tbody>
              ${failRows}
            </tbody>
          </table>
        `;
      }

      this.$q
        .dialog({
          html: true,
          title: "Contacts Upload Complete",
          message,
          persistent: true,
          ok: "OK",
          "full-width": Boolean(failContacts.length),
        })
        .onOk(() => {
          this.cancel();
          this.$emit("confirm", this.contactMailingList);
        });
    },
  },
};
</script>
